import React, {
    memo,
    useEffect,
    useState,
    useRef,
    forwardRef,
    useImperativeHandle
} from "react";
import styles from "../../Styles/Components/text-box.module.scss";

const CustomPercentageInput = forwardRef((props, ref) => {
    const [value, setValue] = useState("");
    const inputRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
                moveCursorBeforePercent();
            }
        }
    }));

    useEffect(() => {
        setValue(props.value || "");
    }, [props.value]);

    const formatDisplayValue = (val) => {
        return val ? `${val}%` : "";
    };

    const moveCursorBeforePercent = () => {
        const el = inputRef.current;
        if (el && value) {
            const pos = String(value).length;
            // Set selection before %
            setTimeout(() => {
                el.setSelectionRange(pos, pos);
            }, 0);
        }
    };

    const _handleChange = (event) => {
        let raw = event.target.value.replace(/[^0-9]/g, "");
        let num = parseInt(raw, 10);

        if (isNaN(num) || num === 0) {
            setValue("");
            props.handleChange("");
        } else if (num >= 100) {
            setValue("100");
            props.handleChange(100);
        } else {
            setValue(String(num));
            props.handleChange(num);
        }
    };

    const _preventInvalidKeys = (e) => {
        const key = e.key;
        const isCtrl = e.ctrlKey || e.metaKey;
        const allowed = ["Backspace", "ArrowLeft", "ArrowRight", "Tab"];

        if (isCtrl && ["a", "c", "x", "v"].includes(key.toLowerCase())) return;
        if (allowed.includes(key)) return;

        // If trying to enter more when value is 100, block
        const currentVal = parseInt(value, 10);
        if (!isNaN(currentVal) && currentVal >= 100) {
            e.preventDefault();
            return;
        }

        // Allow only 0-9
        if (!(key >= "0" && key <= "9")) {
            e.preventDefault();
        }
    };

    const _handleClick = () => {
        moveCursorBeforePercent();
    };

    const _handleFocus = () => {
        moveCursorBeforePercent();
    };

    const _handleKeyUp = () => {
        moveCursorBeforePercent();
    };

    return (
        <div
            className={`${props.className} ${styles.e_input_wrapper} position-relative`}
            id={props.id}
        >
            <p
                className={`e-montserrat-medium color-dark-charcoal e-font-14 e-line-height-18 cursor-text margin-6px-bottom bg-transparent`}
                htmlFor={props.label}
            >
                {props.label}
                <span className="color-deep-carmine-pink">{props.postfix}</span>
            </p>

            <div className="position-relative">
                <input
                    ref={inputRef}
                    required
                    value={formatDisplayValue(value)}
                    id={props.label}
                    type="text"
                    className={`${styles.e_input} color-charleston-green e-montserrat-medium e-font-14 e-line-height-20 w-100 no-outline`}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    autoComplete="off"
                    onChange={_handleChange}
                    onKeyDown={_preventInvalidKeys}
                    onClick={_handleClick}
                    onFocus={_handleFocus}
                    onKeyUp={_handleKeyUp}
                />
            </div>

            {props.error && (
                <div className="position-absolute d-flex align-items-center margin-6px-top w-max-content">
                    <p className="color-deep-carmine-pink e-montserrat-medium e-font-12 e-line-height-16 mb-0">
                        {props.error}
                    </p>
                </div>
            )}
        </div>
    );
});

export default memo(CustomPercentageInput);
